import { createClient, type Session, type User } from '@supabase/supabase-js';

export type { User };

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

/** Supabase client for auth. Returns null if env is not configured (e.g. standalone UI dev). */
function getSupabase() {
  if (!supabaseUrl || !supabaseAnonKey) return null;
  return createClient(supabaseUrl, supabaseAnonKey);
}

const supabase = getSupabase();

export const isAuthConfigured = () => !!supabase;

export async function getSession(): Promise<{ user: User; session: Session } | null> {
  if (!supabase) return null;
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error || !session) return null;
  return { user: session.user, session };
}

export async function signIn(email: string, password: string): Promise<{ error: string | null }> {
  if (!supabase) return { error: 'Auth not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.' };
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  return { error: error?.message ?? null };
}

export async function signUp(email: string, password: string): Promise<{ error: string | null }> {
  if (!supabase) return { error: 'Auth not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.' };
  const { error } = await supabase.auth.signUp({ email, password });
  return { error: error?.message ?? null };
}

export async function signOut(): Promise<void> {
  if (supabase) await supabase.auth.signOut();
}

export function onAuthStateChange(callback: (user: User | null) => void): () => void {
  if (!supabase) return () => {};
  const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
    callback(session?.user ?? null);
  });
  return () => subscription.unsubscribe();
}
